import React, { useEffect, useState } from "react";
import { Crown, Sparkles, X, Check } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function SubscriptionBanner() {
  const { user, checkAuth } = useAuth();
  const [plans, setPlans] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  if (!user || user.role === "admin") return null;
  const status = user.subscription_status || "trial";
  if (status === "active") return null;

  const subscribe = async (tier) => {
    setBusy(true);
    try {
      const r = await api.post("/billing/subscribe", { tier });
      toast.success(`Subscribed · $${r.data.amount_usd}/yr · active until ${new Date(r.data.expires_at).toLocaleDateString()}`);
      await checkAuth();
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Subscription failed");
    } finally { setBusy(false); }
  };

  const tiers = user.role === "agent" ? ["agent"] : ["dmc", "dmc_premium"];

  return (
    <>
      <div
        className="mb-6 bg-gradient-to-r from-brand/10 via-brand/5 to-transparent border border-brand/30 rounded-xl px-5 py-4 flex items-center justify-between flex-wrap gap-3"
        data-testid="subscription-banner"
      >
        <div className="flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-brand" />
          <div>
            <p className="font-display text-white font-bold">You're on a trial — unlock full access.</p>
            <p className="text-xs text-[#a3a3a3] mt-0.5">
              {user.role === "agent" ? "Post unlimited leads · Premium DMC access · AI bid scoring at scale" : "Receive premium leads · Priority placement · Higher conversion"}
            </p>
          </div>
        </div>
        <button onClick={() => setOpen(true)} className="btn-primary text-sm" data-testid="subscribe-open-btn">
          <Crown className="w-4 h-4" /> Subscribe annually
        </button>
      </div>

      {open && plans && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="subscribe-modal">
          <div className="bg-[#0a0a0a] border border-[#262626] rounded-xl w-full max-w-2xl">
            <div className="flex items-center justify-between p-6 border-b border-[#1a1a1a]">
              <div>
                <p className="overline">Annual subscription</p>
                <h3 className="font-display text-2xl font-bold text-white mt-1">Pick your plan</h3>
              </div>
              <button onClick={() => setOpen(false)} className="text-[#a3a3a3] hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 grid sm:grid-cols-2 gap-4">
              {tiers.map((t) => {
                const p = plans[t];
                const isPremium = t === "dmc_premium";
                return (
                  <button
                    key={t}
                    onClick={() => subscribe(t)}
                    disabled={busy}
                    className={`text-left p-6 border rounded-xl transition-all ${isPremium ? "border-brand bg-brand/5 glow-brand" : "border-[#262626] hover:border-[#a3a3a3]"}`}
                    data-testid={`subscribe-${t}`}
                  >
                    {isPremium && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-brand mb-2">
                        <Crown className="w-3 h-3" /> Most popular
                      </span>
                    )}
                    <p className="font-display text-white text-lg font-bold">{p.label}</p>
                    <p className="font-display text-4xl font-black text-white mt-3">
                      ${p.amount_usd}<span className="text-base font-normal text-[#737373]">/yr</span>
                    </p>
                    <ul className="mt-4 space-y-2 text-xs text-[#a3a3a3]">
                      {(t === "agent" ? [
                        "Unlimited lead posting",
                        "Access to all 50+ countries",
                        "AI bid scoring & apples-check",
                        "10% escrow on every booking",
                      ] : t === "dmc" ? [
                        "Receive matched leads",
                        "Place unlimited bids",
                        "Standard listing in DMC catalog",
                        "Get paid via escrow",
                      ] : [
                        "Everything in DMC Standard",
                        "Priority placement + AI score boost",
                        "Premium badge on every bid",
                        "Featured on landing page",
                      ]).map((b) => (
                        <li key={b} className="flex items-start gap-1.5">
                          <Check className="w-3 h-3 text-brand flex-shrink-0 mt-0.5" /> {b}
                        </li>
                      ))}
                    </ul>
                    <span className="inline-block mt-5 text-sm text-brand font-mono uppercase tracking-widest">
                      {busy ? "Processing…" : "Activate →"}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="text-center text-xs text-[#737373] pb-5">Simulated billing for demo. Production: Stripe.</p>
          </div>
        </div>
      )}
    </>
  );
}
